import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { useSeoMeta } from '@unhead/react';
import { Pencil, ArrowLeft, Trash2 } from 'lucide-react';

import { AppShell } from '@/components/AppShell';
import { EditAlbumDialog } from '@/components/EditAlbumDialog';
import { ScrapbookPages } from '@/components/ScrapbookPages';
import { WashiTape } from '@/components/WashiTape';
import { DateDoodle } from '@/components/DateDoodle';
import { Skeleton } from '@/components/ui/skeleton';
import { useDittoThemeContext } from '@/components/DittoThemeProvider';
import { useAlbum } from '@/hooks/useAlbum';
import { useAlbumActions } from '@/hooks/useAlbumActions';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useDittoTheme } from '@/hooks/useDittoTheme';
import { useToast } from '@/hooks/useToast';
import { parseThemeCoordinate } from '@/lib/album-utils';
import { genUserName } from '@/lib/genUserName';
import { formatPolaroidDate, getPastelStripColor, seedFromHex, getTapeVariant } from '@/lib/polaroid-utils';
import { cn } from '@/lib/utils';
import NotFound from './NotFound';

/** Tape solid colors — CSS variables that adapt to theme */
const TAPE_SOLID_VARS = [
  'var(--tape-0-solid)', 'var(--tape-1-solid)', 'var(--tape-2-solid)', 'var(--tape-3-solid)',
  'var(--tape-4-solid)', 'var(--tape-5-solid)', 'var(--tape-6-solid)', 'var(--tape-7-solid)',
];

/** Ruled notebook lines background */
const RULED_LINES = 'repeating-linear-gradient(to bottom, transparent, transparent 19px, rgba(180,200,220,0.25) 19px, rgba(180,200,220,0.25) 20px)';

export default function AlbumDetail() {
  const { npub, slug } = useParams<{ npub: string; slug: string }>();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const { toast } = useToast();
  const { setOverrideTheme, setForceDefault, hasBgImage } = useDittoThemeContext();
  const { deleteAlbum } = useAlbumActions();
  const [editOpen, setEditOpen] = useState(false);

  // Decode npub
  let pubkey: string | undefined;
  try {
    if (npub) {
      const decoded = nip19.decode(npub);
      if (decoded.type === 'npub') pubkey = decoded.data as string;
      else if (decoded.type === 'nprofile') pubkey = (decoded.data as { pubkey: string }).pubkey;
    }
  } catch {
    // invalid npub
  }

  const { data, isLoading } = useAlbum(pubkey, slug);
  const album = data?.album;
  const pictures = data?.pictures ?? [];
  const isOwnAlbum = !!user && !!album && user.pubkey === album.pubkey;

  // Resolve the album's theme coordinate (album-specific theme)
  const themeCoord = useMemo(() => {
    if (!album?.themeCoordinate) return undefined;
    return parseThemeCoordinate(album.themeCoordinate);
  }, [album?.themeCoordinate]);

  // Fetch album-specific theme (if set) and album author's profile theme
  const { data: albumSpecificTheme } = useDittoTheme(themeCoord?.pubkey);
  const { data: authorProfileTheme } = useDittoTheme(pubkey);

  // Author info
  const author = useAuthor(pubkey);
  const displayName = author.data?.metadata?.name ?? genUserName(pubkey ?? '');

  // Theme priority:
  // 1. Album-specific theme (from `theme` tag) — highest priority
  // 2. Album author's profile theme (kind 16767) — fallback
  // 3. No theme (force default) — if author has no theme either
  const isOwnProfile = user?.pubkey === pubkey;

  useEffect(() => {
    if (albumSpecificTheme) {
      // Album has its own theme — use it
      setOverrideTheme(albumSpecificTheme);
    } else if (authorProfileTheme && !isOwnProfile) {
      // No album theme, but the author has a profile theme — use that
      // (Skip override for own profile since the global provider already applies it)
      setOverrideTheme(authorProfileTheme);
    } else if (!isOwnProfile && !authorProfileTheme) {
      // Author has no theme at all — force default so logged-in user's
      // theme doesn't bleed through to someone else's album
      setForceDefault(true);
    }

    return () => {
      setOverrideTheme(undefined);
      setForceDefault(false);
    };
  }, [albumSpecificTheme, authorProfileTheme, isOwnProfile, setOverrideTheme, setForceDefault]);

  useSeoMeta({
    title: album ? `${album.title} — Polaroids` : 'Album — Polaroids',
    description: album?.description ?? `Photo album on Polaroids`,
  });

  const handleDelete = async () => {
    if (!album || !confirm('Delete this album? The photos inside will not be deleted.')) return;
    try {
      await deleteAlbum(album.dTag);
      toast({ title: 'Album deleted' });
      navigate(-1);
    } catch (error) {
      console.error('Delete album failed:', error);
      toast({ title: 'Failed to delete album', description: 'Please try again.' });
    }
  };

  if (!pubkey || !slug) return <NotFound />;

  if (isLoading) {
    return (
      <AppShell>
        <div className="py-6 space-y-4">
          <Skeleton className="h-8 w-48 skeleton-shimmer animate-shimmer" />
          <Skeleton className="aspect-[5/3] w-full skeleton-shimmer animate-shimmer" />
          <div className="space-y-2">
            <Skeleton className="h-5 w-32 skeleton-shimmer animate-shimmer" />
            <Skeleton className="h-4 w-64 skeleton-shimmer animate-shimmer" />
          </div>
          <Skeleton className="aspect-square w-full skeleton-shimmer animate-shimmer" />
        </div>
      </AppShell>
    );
  }

  if (!album) return <NotFound />;

  const coverRotation = (seedFromHex(album.event.id, 10, 14) % 5) - 2;
  const tapeIdx = getTapeVariant(album.event.id);
  const tapeColor = TAPE_SOLID_VARS[tapeIdx % TAPE_SOLID_VARS.length];
  const tapeRotation = (seedFromHex(album.event.id, 6, 8) % 10) - 5;

  return (
    <AppShell>
      <div className="py-6 space-y-6">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className={cn(
            'flex items-center gap-1.5 font-hand text-pol-text-secondary hover:text-pol-text transition-colors active:scale-95',
            hasBgImage ? 'paper-strip' : 'paper-strip-pastel',
          )}
          style={hasBgImage ? undefined : { backgroundColor: getPastelStripColor(album.event.id + 'back') }}
        >
          <ArrowLeft size={16} />
          <span>Back</span>
        </button>

        {/* Album cover hero — polaroid card style with cover image */}
        <div style={{ transform: `rotate(${coverRotation * 0.5}deg)` }}>
          <div className="relative bg-polaroid-white p-3 pb-0 shadow-[0_4px_20px_rgba(45,27,78,0.12)]">
            {/* Washi tape */}
            <WashiTape eventId={album.event.id} size="lg" />

            {/* Cover image */}
            {album.image ? (
              <div className="relative overflow-hidden bg-cream-dark aspect-[5/3]">
                <img
                  src={album.image}
                  alt={album.title}
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              <div className="relative overflow-hidden aspect-[5/3] flex items-center justify-center" style={{ backgroundColor: tapeColor, opacity: 0.15 }}>
                <svg width="64" height="64" viewBox="0 0 48 48" fill="none" className="opacity-40 text-pol-text-secondary">
                  <rect x="8" y="6" width="20" height="24" fill="currentColor" stroke="currentColor" strokeWidth="1.5" />
                  <rect x="20" y="12" width="20" height="24" fill="white" stroke="currentColor" strokeWidth="1.5" />
                </svg>
              </div>
            )}

            {/* Title + info inside the polaroid strip */}
            <div className="px-1 pt-2 pb-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <h1 className="font-hand text-2xl font-bold text-pol-text leading-tight">
                    {album.title}
                  </h1>
                  <p className="font-hand font-bold text-sm text-pol-text-caption mt-0.5">
                    {album.photoIds.length} {album.photoIds.length === 1 ? 'photo' : 'photos'}
                    {' · '}
                    {formatPolaroidDate(album.createdAt)}
                  </p>
                </div>

                {/* Actions */}
                {isOwnAlbum && (
                  <div className="flex items-center gap-1 flex-shrink-0 mt-1">
                    <button
                      onClick={() => setEditOpen(true)}
                      className="w-8 h-8 flex items-center justify-center text-pol-text-secondary hover:text-pol-text transition-colors active:scale-90"
                      aria-label="Edit album"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={handleDelete}
                      className="w-8 h-8 flex items-center justify-center text-pol-text-secondary hover:text-red-500 transition-colors active:scale-90"
                      aria-label="Delete album"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                )}
              </div>

              {/* Author */}
              <button
                onClick={() => navigate(`/${npub}`)}
                className="flex items-center gap-1.5 mt-1.5 group"
              >
                <div className="w-5 h-5 rounded-full overflow-hidden bg-cream-dark border border-pol-pink/30 flex-shrink-0">
                  {author.data?.metadata?.picture ? (
                    <img src={author.data.metadata.picture} alt={displayName} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-pol-pink/40 to-pol-lilac/40" />
                  )}
                </div>
                <span className="text-[12px] text-pol-text-secondary group-hover:text-pol-text transition-colors">
                  {displayName}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Description — notebook paper style */}
        {album.description && (() => {
          const descRotation = (seedFromHex(album.event.id, 20, 24) % 5) - 2;
          const descTapeRotation = (seedFromHex(album.event.id, 14, 16) % 10) - 5;

          return (
            <div className="max-w-[420px]" style={{ transform: `rotate(${descRotation}deg)` }}>
              <div className="relative">
                {/* Tape strip */}
                <div
                  className="absolute left-1/2 z-10"
                  style={{
                    width: '80px',
                    height: '20px',
                    top: '-10px',
                    backgroundColor: tapeColor,
                    opacity: 0.7,
                    transform: `translateX(-50%) rotate(${descTapeRotation}deg)`,
                  }}
                />

                {/* Notebook paper body */}
                <div
                  className="relative px-4 pt-5 pb-4"
                  style={{
                    backgroundColor: '#FFFFFF',
                    backgroundImage: RULED_LINES,
                    backgroundPosition: '0 10px',
                    boxShadow: '1px 2px 6px rgba(0,0,0,0.08)',
                  }}
                >
                  <p
                    className="font-hand text-base leading-[20px] break-words overflow-hidden"
                    style={{ color: '#2D1B4E', overflowWrap: 'anywhere' }}
                  >
                    {album.description}
                  </p>
                </div>

                {/* Peeling corner */}
                <div className="absolute bottom-0 right-0 overflow-hidden" style={{ width: '20px', height: '20px' }}>
                  <div className="absolute inset-0" style={{ backgroundColor: '#FDF6EC' }} />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, transparent 50%, #E8E8E8 50%)' }} />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, transparent 45%, rgba(0,0,0,0.08) 50%, transparent 55%)' }} />
                </div>
              </div>
            </div>
          );
        })()}

        {/* Photos — scrapbook pages */}
        {pictures.length > 0 ? (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <DateDoodle seed={album.event.id + 'photos'} size={22} />
              <span
                className={cn(
                  'font-hand font-bold text-xl text-pol-text',
                  hasBgImage ? 'paper-strip' : 'paper-strip-pastel',
                )}
                style={hasBgImage ? undefined : { backgroundColor: getPastelStripColor(album.event.id + 'photos-label') }}
              >
                Photos
              </span>
            </div>
            <ScrapbookPages pictures={pictures} />
          </div>
        ) : (
          <div className="bg-polaroid-white p-6 text-center border-2 border-dashed border-cream-dark/50">
            <p className="font-hand text-lg text-pol-text-secondary">
              No photos in this album yet
            </p>
            {isOwnAlbum && (
              <p className="text-sm text-pol-text-secondary/60 mt-1">
                Edit this album to add photos
              </p>
            )}
          </div>
        )}
      </div>

      {/* Edit album dialog */}
      {album && isOwnAlbum && (
        <EditAlbumDialog
          album={album}
          open={editOpen}
          onOpenChange={setEditOpen}
        />
      )}
    </AppShell>
  );
}
